import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TerminalIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { proBuilderKeys } from '~/lib/query-keys';
import { Button } from '~/components/ui/button';
import { usePtyTerminal, type PtyOpenResponse } from '../terminal/use-pty-terminal';
import { PRO_BUILDER_COPY_REFUSED, PRO_BUILDER_TERMINAL_ENDED, PRO_BUILDER_TERMINAL_FAILED, PRO_BUILDER_TERMINAL_RECONNECTING } from './pro-builder-copy';
import { useProBuilderSessionStore } from './pro-builder-session-store';
import { endsSessionOnTeardown, recordOpenedSession } from './pro-builder-terminal-session';
import { proBuilderTerminalStatusText, proBuilderTerminalView } from './pro-builder-terminal-view';
import { proBuilderTerminalEndpoints } from './terminal-endpoints';
import { ProBuilderHeaderBar } from './pro-builder-window';
import { ProBuilderConsentSlot } from './pro-builder-consent-card';
import { ProBuilderDropZone } from './pro-builder-drop-zone';

/** A browser that refuses the clipboard write leaves the old clipboard in place: say so, and point at the link (FRONT-3172). */
function notifyCopyRefused() {
  toast.error(PRO_BUILDER_COPY_REFUSED, { id: 'pro-builder-copy-refused' });
}

/**
 * The person's own Claude Code terminal (FRONT-3003) over the shared
 * PTY-over-SSE hook, filling the Pro Builder window's body edge to edge on the
 * panel's own colour. Unmounting only DETACHES (the shell lives on and the
 * next open attaches to it); only a restart from Settings ends the shell. The
 * window mounts this only when Pro Builder is on for the organization, and
 * hiding the window never unmounts it. It is the one terminal that opts in
 * to the clipboard handling (`clipboard: true`): OSC 52 writes after the
 * person's own key press or mouse action, so Claude Code's "c to copy" puts its
 * sign-in URL on the clipboard in one piece and its full-screen mouse selection
 * lands too, plus Cmd+C / right-click Copy of a selection, cleaned of the
 * window's line breaks and margin — and a toast when the browser refuses such a
 * write. It is also the one whose links open with a click, that URL included
 * although it is hard-wrapped over several rows (FRONT-3172), and the one that
 * follows Eddy's light/dark theme and its monospace font.
 *
 * It also keeps the session store honest about what is running: after every
 * open (first open, Reconnect, the back/forward-cache restart, a restart from
 * Settings, an attach to a shell that survived a reload) it records what that
 * shell was started with, as the open response reports it, and on unmount it
 * records that nothing is open — so Settings offers a Restart exactly when the
 * running shell carries a credential other than the saved one.
 *
 * The header bar carries the crumb, the app switcher, New, "Runs Claude
 * Code", the context meter, the Save disc (for people who may build apps) and
 * the close button — nothing about the connection. The connection status is a screen-reader-only live element
 * (no lamp) that says the same thing as what is shown
 * (`pro-builder-terminal-view.ts`): an ended or failed terminal says so inside
 * the terminal body with the Reconnect button there, and a dropped connection
 * that is still being retried shows only a quiet "Reconnecting…" line.
 */
export function ProBuilderTerminal({ onClose, terminalKey }: { onClose: () => void; terminalKey: number }) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const endpoints = useMemo(() => proBuilderTerminalEndpoints(), []);
  const dispatch = useProBuilderSessionStore((s) => s.dispatch);
  const qc = useQueryClient();
  // Each open gets a number; only the newest one, while mounted, may record.
  const openGeneration = useRef(0);
  const onSessionOpened = useCallback(
    (opened: PtyOpenResponse) => {
      const generation = ++openGeneration.current;
      recordOpenedSession({ opened, isCurrent: () => openGeneration.current === generation, dispatch });
      // The shell opens in the folder the SERVER has bound now; a second tab may have switched app since this header last read it.
      void qc.invalidateQueries({ queryKey: proBuilderKeys.app() });
      // The header's "Copy last answer" asks by session id: read the NEW shell's id now, not at the next poll (~15 s).
      void qc.invalidateQueries({ queryKey: proBuilderKeys.session() });
    },
    [dispatch, qc],
  );
  // Unmount = this terminal is gone (org switch off, flag off, or a restart replacing it); the shell is ended only by the restart.
  useEffect(
    () => () => {
      openGeneration.current += 1;
      dispatch({ type: 'terminal-stopped' });
    },
    [dispatch],
  );

  const { state, errorMessage, reconnecting, reconnect } = usePtyTerminal({
    container,
    endpoints,
    clipboard: true,
    // Claude Code's sign-in URL is hard-wrapped over several rows: one click opens all of it (FRONT-3172).
    links: true,
    onCopyRefused: notifyCopyRefused,
    appearance: 'follow-app',
    // A hidden window still holds a running Claude Code; keep the daemon's
    // idle reaper from ending it while the person works elsewhere.
    keepAlive: true,
    // The daemon does not replay PTY output to a late stream: nudge the size so Claude Code repaints.
    redrawOnReattach: true,
    onSessionOpened,
    // FRONT-3040: the session outlives this component (reload, tab close, the
    // window unmounting); the next open attaches to it. The ONE teardown that
    // must still end the shell is a restart, which has already bumped the
    // store's terminalKey past the one this terminal was mounted with.
    endSessionOnTeardown: () => endsSessionOnTeardown(terminalKey, useProBuilderSessionStore.getState().session.terminalKey),
  });
  const view = proBuilderTerminalView(state, errorMessage, reconnecting);

  return (
    <>
      <ProBuilderHeaderBar onClose={onClose} view="terminal" />
      <span role="status" className="sr-only" data-testid="pro-builder-terminal-status">
        {proBuilderTerminalStatusText(view, state)}
      </span>
      <ProBuilderDropZone>
      <div data-testid="pro-builder-terminal" className="relative flex min-h-[320px] w-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
        {/* Padding lives on this OUTER box: the fit addon reads the container's
            computed height (border-box, so padding included) and subtracts only
            the xterm element's own padding, so padding on the container itself
            fits one row too many and clips the bottom line. */}
        <div className="flex min-h-0 w-full flex-1 flex-col p-2">
          <div ref={setContainer} data-testid="pro-builder-terminal-host" className="min-h-0 w-full flex-1" />
        </div>
        {view.kind === 'reconnecting' && (
          // Non-blocking: no backdrop, no button, clicks pass through. The live
          // region above already announces it, so this copy is hidden from it.
          <div
            aria-hidden
            data-testid="pro-builder-terminal-reconnecting"
            className="pointer-events-none absolute right-3 top-3 rounded-md border border-border bg-card/90 px-2 py-1 text-xs text-muted-foreground"
          >
            {PRO_BUILDER_TERMINAL_RECONNECTING}
          </div>
        )}
        {(view.kind === 'ended' || view.kind === 'failed') && (
          <EndedOverlay message={view.kind === 'failed' ? view.message : null} onReconnect={reconnect} />
        )}
        {/* FRONT-3176: the OK card spotlights over the terminal, which is paused while it waits. */}
        <ProBuilderConsentSlot />
      </div>
      </ProBuilderDropZone>
    </>
  );
}

/**
 * Calm centered message over the (kept-mounted) terminal; token colours so it
 * reads in light and dark. It takes the keyboard when it appears: xterm
 * captures Tab, so a keyboard user could not otherwise reach Reconnect. A
 * button inside a hidden window cannot take focus, so the window's own open
 * focus also prefers this button (`data-pro-builder-autofocus`).
 */
function EndedOverlay({ message, onReconnect }: { message: string | null; onReconnect: () => void }) {
  const reconnectRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    reconnectRef.current?.focus();
  }, []);
  return (
    <div
      data-testid="pro-builder-terminal-ended"
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/90 p-6 text-center"
    >
      <TerminalIcon size={20} className="text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">{message ? PRO_BUILDER_TERMINAL_FAILED : PRO_BUILDER_TERMINAL_ENDED}</p>
      {message && <p className="text-xs text-muted-foreground">{message}</p>}
      <Button
        ref={reconnectRef}
        variant="outline"
        size="sm"
        data-testid="pro-builder-reconnect"
        data-pro-builder-autofocus=""
        onClick={onReconnect}
      >
        Reconnect
      </Button>
    </div>
  );
}
